// src/pages/subscription/TenantSubscribePage.tsx

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';

export const TenantSubscribePage = observer(() => {
  const navigate = useNavigate();
  const store = useSubscription();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: '',
    product_id: '',
    plan_id: '',
    seats: '',
    config: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload: any = {
        plan: { id: formData.plan_id },
        seats: formData.seats ? Number(formData.seats) : undefined,
        config: formData.config ? JSON.parse(formData.config) : undefined,
      };
      await store.SubscribeTenant(formData.tenant_id, formData.product_id, payload);
      navigate('/subscriptions/tenants');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Subscribe Tenant</h1>
        <nav className="text-sm">
          <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/subscriptions/tenants" className="text-muted-foreground hover:text-foreground">Tenant Subscriptions</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Subscribe</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Subscription Details</CardTitle>
            <CardDescription>Subscribe a tenant to a product plan</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input label="Tenant ID" type="text" value={formData.tenant_id} onChange={(e) => handleChange('tenant_id', e.target.value)} placeholder="Tenant identifier" required />
            <Input label="Product ID" type="text" value={formData.product_id} onChange={(e) => handleChange('product_id', e.target.value)} placeholder="Product identifier" required />
            <Input label="Plan ID" type="text" value={formData.plan_id} onChange={(e) => handleChange('plan_id', e.target.value)} placeholder="Plan identifier" required />
            <Input label="Seats" type="number" value={formData.seats} onChange={(e) => handleChange('seats', e.target.value)} placeholder="Number of seats" />

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Configuration (JSON)</label>
              <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring font-mono min-h-[80px]" value={formData.config} onChange={(e) => handleChange('config', e.target.value)} placeholder='{"feature_flags": {"advanced_analytics": true}}' rows={4} />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to="/subscriptions/tenants"><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.creating') : 'Subscribe Tenant'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default TenantSubscribePage;
